import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/context/AuthContext'
import { useLastSeenHeartbeat } from '@/data/crm/queries/presence'
import { useTranslation } from '@/i18n'
import { notifyNewMessage } from '@/lib/chatNotify'
import { usePushBridge } from '@/hooks/usePushBridge'
import { attachLiveTables } from '@/data/crm/live'

interface CrmUnreadValue {
  /** thread id -> unread message count */
  unreadByThread: Record<string, number>
  totalUnread: number
  markThreadRead: (threadId: string) => void
}

const CrmUnreadContext = createContext<CrmUnreadValue | null>(null)

async function fetchUnread(): Promise<Record<string, number>> {
  const { data, error } = await supabase.rpc('unread_counts')
  if (error) throw error
  const result: Record<string, number> = {}
  for (const row of data ?? []) result[row.thread_id] = Number(row.unread_count)
  return result
}

export function CrmUnreadProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { t } = useTranslation()
  // Read inside the realtime handlers without resubscribing on a language change.
  const tRef = useRef(t)
  tRef.current = t
  // Wraps every CRM and portal page, which is exactly where "online" should mean "has the app open".
  useLastSeenHeartbeat()
  usePushBridge(user?.id)

  const { data: unreadByThread = {} } = useQuery({
    queryKey: ['unread', user?.id],
    enabled: !!user,
    queryFn: fetchUnread,
    // Realtime is the primary signal below; this is just a safety net.
    refetchInterval: 120_000,
  })

  // Live-update the inboxes and the activity feeds off new rows, without every
  // viewer polling. One channel for all three: a Supabase channel is a
  // websocket subscription, and three would be three.
  //
  // Keyed on the user id, not the user object: the object is replaced on every
  // token refresh, and each replacement tore the channel down and rebuilt it.
  // Each subscription also gets its own topic. Rejoining 'crm-live' while the
  // previous 'crm-live' was still leaving lost the server's reply, so the join
  // timed out and nothing arrived (seen 1 November 2026; React's double effect
  // in development did it on every load).
  const userId = user?.id
  useEffect(() => {
    if (!userId) return
    let joined = false
    const channel = supabase
      .channel(`crm-live:${userId}:${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'communications' },
        payload => {
          const row = payload.new as { thread_id: string; author_id: string | null; body: string; mentions?: string[] | null }
          const threadId = row.thread_id
          if (row.author_id !== userId) {
            const mentioned = (row.mentions ?? []).includes(userId)
            notifyNewMessage({
              threadId,
              title: mentioned ? tRef.current.chat.mentionedYou : tRef.current.chat.newMessage,
              body: row.body || '📎',
            })
          }
          qc.invalidateQueries({ queryKey: ['unread', userId] })
          qc.invalidateQueries({ queryKey: ['threads'] })
          qc.invalidateQueries({ queryKey: ['thread-messages', threadId] })
          // A new message is the moment "Seen" is most likely to change next.
          qc.invalidateQueries({ queryKey: ['thread-reads', threadId] })
        }
      )
      // Edits, unsends and pins are UPDATEs (edit_message, unsend_message,
      // set_message_pin). The thread list too, because the preview follows an
      // edit of the latest message.
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'communications' },
        payload => {
          const threadId = (payload.new as { thread_id: string }).thread_id
          qc.invalidateQueries({ queryKey: ['threads'] })
          qc.invalidateQueries({ queryKey: ['thread-messages', threadId] })
          qc.invalidateQueries({ queryKey: ['pinned-messages', threadId] })
        }
      )
      // A reaction row carries no thread id, so every loaded conversation is
      // marked stale; only the open one actually refetches.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_reactions' }, () => {
        qc.invalidateQueries({ queryKey: ['thread-messages'] })
      })
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'support_messages' },
        payload => {
          const threadId = (payload.new as { thread_id: string }).thread_id
          qc.invalidateQueries({ queryKey: ['support-threads'] })
          qc.invalidateQueries({ queryKey: ['support-messages', threadId] })
        }
      )
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_message_reactions' }, () => {
        qc.invalidateQueries({ queryKey: ['support-messages'] })
      })
      // Visitor support chat rides the same channel rather than opening a
      // second one. Safe to subscribe to: Realtime authorises against RLS as the
      // subscribing role, and support_messages is readable by staff only.
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_messages' },
        payload => {
          const row = payload.new as { thread_id: string; sender: string; body: string }
          const threadId = row.thread_id
          if (row.sender === 'visitor') {
            notifyNewMessage({
              threadId: `support:${threadId}`,
              title: tRef.current.chat.newSupportMessage,
              body: row.body || '📎',
            })
          }
          qc.invalidateQueries({ queryKey: ['support-threads'] })
          qc.invalidateQueries({ queryKey: ['support-messages', threadId] })
          qc.invalidateQueries({ queryKey: ['crm-badge-counts'] })
        }
      )
      // Project and task activity feeds, on the same channel for the same
      // reason. Invalidated by prefix rather than by entity: only an open feed
      // has a live query to refetch, so narrowing the key would buy nothing.
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'activity_events' },
        () => {
          qc.invalidateQueries({ queryKey: ['activity-feed'] })
        }
      )
    // Tasks, projects, notes, clients, files, orders and deliveries, on the
    // same channel for the same reason (src/data/crm/live.ts).
    const live = attachLiveTables(channel, qc)
    channel.subscribe(status => {
      if (status !== 'SUBSCRIBED') return
      // A rejoin follows a dropped connection (a background tab, a network
      // blip), and whatever changed meanwhile was never delivered: catch up.
      if (joined) {
        qc.invalidateQueries({ queryKey: ['unread', userId] })
        qc.invalidateQueries({ queryKey: ['threads'] })
        qc.invalidateQueries({ queryKey: ['support-threads'] })
        live.catchUp()
      }
      joined = true
    })
    return () => {
      live.dispose()
      supabase.removeChannel(channel)
    }
  }, [userId, qc])

  const markThreadRead = useCallback(
    (threadId: string) => {
      if (!user) return
      supabase
        .from('message_reads')
        .upsert({ thread_id: threadId, user_id: user.id, last_read_at: new Date().toISOString() }, { onConflict: 'thread_id,user_id' })
        .then(() => qc.invalidateQueries({ queryKey: ['unread', user.id] }))
    },
    [user, qc]
  )

  const value = useMemo<CrmUnreadValue>(
    () => ({
      unreadByThread,
      totalUnread: Object.values(unreadByThread).reduce((sum, n) => sum + n, 0),
      markThreadRead,
    }),
    [unreadByThread, markThreadRead]
  )

  return <CrmUnreadContext.Provider value={value}>{children}</CrmUnreadContext.Provider>
}

export function useCrmUnread() {
  const ctx = useContext(CrmUnreadContext)
  if (!ctx) {
    // Fallback keeps non-CRM surfaces working without the provider.
    return {
      unreadByThread: {} as Record<string, number>,
      totalUnread: 0,
      markThreadRead: () => {},
    } satisfies CrmUnreadValue
  }
  return ctx
}
